import { Injectable, OnDestroy } from '@angular/core';
import { ActiveToast, ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UserLogin } from 'app/models/UserLogin';
import { UserService } from 'app/services/user.service';
import { readJwtClaims } from 'app/services/jwt-claims.util';

/** How far ahead of the cap the warning appears. */
const WARNING_LEAD_SECONDS = 5 * 60;

/**
 * Polled against the wall clock rather than armed as a single timer to the deadline. A laptop
 * that sleeps suspends timers but not the passage of time, so a timer set at login would fire
 * late by however long the machine was closed. A twelve-hour `setTimeout` is also past the point
 * where the delay is worth trusting, and an environment configured with a longer cap would
 * overflow it outright and fire immediately.
 */
const POLL_INTERVAL_MS = 30_000;

/**
 * Sticky, manually dismissible, and deliberately not in toastr's default corner.
 *
 * Sticky because a prompt to save work cannot be the kind that vanishes while it is being read.
 * That is also what makes the position matter: every other toast in this app clears itself within
 * seconds, so none of them ever sits over anything. These two persist for minutes, and the default
 * `toast-top-right` is exactly where the navbar's own menu is, so the notice would cover the
 * controls it is telling the user to go and use. Bottom right belongs to the assistant button, so
 * bottom left is the corner that is actually free.
 */
const SESSION_TOAST_OPTIONS = {
	disableTimeOut: true,
	closeButton: true,
	tapToDismiss: false,
	positionClass: 'toast-bottom-left',
};

/**
 * Warns the user before their session reaches its absolute cap.
 *
 * The cap is deliberately not extended by activity, so someone mid-form can otherwise be sent to
 * the login screen with no notice. This does not prevent that or delay it; it only makes it
 * predictable, which is the difference between "the portal logged me out" and "I saved first".
 */
@Injectable({ providedIn: 'root' })
export class SessionExpiryWarningService implements OnDestroy {
	private poll: ReturnType<typeof setInterval> | null = null;
	private toast: ActiveToast<unknown> | null = null;
	/** The deadline already warned about, so one session raises one toast. */
	private warnedFor: number | null = null;
	/** The deadline already reported as passed, so the closing notice is raised once. */
	private expiredFor: number | null = null;
	private readonly destroy$ = new Subject<void>();

	constructor(private readonly userService: UserService, private readonly toastr: ToastrService) {}

	/**
	 * Begins watching the stored session. Safe to call more than once; only the first call arms
	 * the poll.
	 */
	start() {
		if (this.poll !== null) {
			return;
		}

		// Signing in, signing out and switching client all republish the session. Reacting to that
		// is what clears a stale warning promptly instead of leaving it up to the next poll.
		this.userService.currentUser.pipe(takeUntil(this.destroy$)).subscribe(() => this.onSessionChanged());

		this.poll = setInterval(() => this.check(), POLL_INTERVAL_MS);
		this.check();
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
		if (this.poll !== null) {
			clearInterval(this.poll);
			this.poll = null;
		}
		this.dismiss();
	}

	private onSessionChanged() {
		// A silent refresh restates the same deadline, so the warning must survive it. Only a
		// genuinely different session (a new login, a sign-out) resets the warned state.
		const deadline = this.deadlineOf(this.userService.currentUserValue);
		if (deadline !== this.warnedFor && deadline !== this.expiredFor) {
			this.dismiss();
			this.warnedFor = null;
			this.expiredFor = null;
		}
		this.check();
	}

	private check() {
		const session = this.userService.currentUserValue;
		const remaining = this.remainingSeconds(session);

		if (remaining === null) {
			// No session, or one that predates the server sending a deadline. Nothing to say.
			this.dismiss();
			return;
		}

		const deadline = this.deadlineOf(session);
		if (deadline === null) {
			return;
		}

		if (remaining <= 0) {
			// Past the cap. Simply clearing the countdown here would be read as the danger having
			// passed, when the opposite is true: the session is over and the next request is the
			// one that ends it. Say so instead of going quiet.
			if (this.expiredFor !== deadline) {
				this.expiredFor = deadline;
				this.dismiss();
				this.raiseExpired();
			}
			return;
		}

		if (remaining > WARNING_LEAD_SECONDS) {
			return;
		}

		if (deadline === this.warnedFor) {
			return;
		}

		this.warnedFor = deadline;
		this.raise(deadline);
	}

	/**
	 * Reports a session that has already reached its cap.
	 *
	 * Deliberately a notice and not a redirect. The client's arithmetic decides when this appears,
	 * and signing someone out on the strength of a local calculation is the failure this ticket
	 * exists to prevent. The server still owns the decision; this only stops the screen from
	 * looking healthy while it is not.
	 */
	private raiseExpired() {
		this.toast = this.toastr.warning(
			'Your session has ended. Your next action will take you to the sign-in screen.',
			'Session ended',
			SESSION_TOAST_OPTIONS
		);
	}

	private raise(deadline: number) {
		const endsAt = new Date(deadline).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
		this.toast = this.toastr.warning(
			`Your session ends at ${endsAt}. Save your work and sign in again to keep working.`,
			'Session ending soon',
			SESSION_TOAST_OPTIONS
		);
	}

	private dismiss() {
		if (this.toast) {
			this.toastr.clear(this.toast.toastId);
			this.toast = null;
		}
	}

	/**
	 * Seconds left before the cap, or null when there is no session or no deadline to read.
	 *
	 * Measured from when the token was received rather than straight off the browser clock. `iat`
	 * is the server's own view of when it minted the token, so `deadline - iat` is a pure
	 * server-side duration, and only the short elapsed span since receipt is taken locally. A
	 * browser whose clock is wrong by hours therefore still warns at the right moment.
	 */
	private remainingSeconds(session: UserLogin | null): number | null {
		const deadline = this.deadlineOf(session);
		if (deadline === null) {
			return null;
		}

		const issuedAt = Number(readJwtClaims(session.token)?.iat);
		if (Number.isFinite(issuedAt) && Number.isFinite(session.receivedAt)) {
			const sessionWindow = deadline / 1000 - issuedAt;
			return sessionWindow - (Date.now() - session.receivedAt) / 1000;
		}

		return (deadline - Date.now()) / 1000;
	}

	/** The cap as epoch milliseconds, or null when the session does not carry one. */
	private deadlineOf(session: UserLogin | null): number | null {
		const raw = session?.sessionExpiresAt;
		if (!raw) {
			return null;
		}

		// The server stamps this UTC, but a value serialised without a zone designator would be
		// read as local time and shift the deadline by the browser's offset.
		const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw);
		const parsed = Date.parse(hasZone ? raw : `${raw}Z`);
		return Number.isFinite(parsed) ? parsed : null;
	}
}
